import { useCallback, useEffect, useState } from 'react';
import i18n from 'i18n/config';
import { translate } from 'hooks/useTranslate';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import {
  EncumbranceOption,
  encumbranceOptions,
  EncumbranceTypes,
} from 'modules/partners/constants/encumbranceOptions';

type UseGetEncumbranceTypeOptionsProps = {
  defaultEncumbranceType?: string;
};

const mapOptionStatic = (
  encumbranceOption: EncumbranceOption
): CustomAsyncSelectOption => ({
  value: encumbranceOption.value,
  label: translate(`ShareEncumbranceOption_${encumbranceOption.label}`),
});

const findOptionByValueStatic = (value: EncumbranceTypes | '') => {
  const found = encumbranceOptions.find((option) => option.value === value);
  return found ? mapOptionStatic(found) : null;
};

export const useGetEncumbranceTypeOptions = ({
  defaultEncumbranceType = '',
}: UseGetEncumbranceTypeOptionsProps = {}) => {
  const [language, setLanguage] = useState(i18n.language);
  const [selectedEncumbranceType, setSelectedEncumbranceType] = useState<
    EncumbranceTypes | ''
  >(defaultEncumbranceType as EncumbranceTypes | '');
  const [selectedEncumbranceTypeOption, setSelectedEncumbranceTypeOption] =
    useState<CustomAsyncSelectOption | null>(() =>
      findOptionByValueStatic(defaultEncumbranceType as EncumbranceTypes | '')
    );

  const mapOption = useCallback(
    (encumbranceOption: EncumbranceOption): CustomAsyncSelectOption => ({
      value: encumbranceOption.value,
      label: translate(`ShareEncumbranceOption_${encumbranceOption.label}`),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const findOptionByValue = useCallback(
    (value: EncumbranceTypes | '') => {
      const found = encumbranceOptions.find((option) => option.value === value);
      return found ? mapOption(found) : null;
    },
    [mapOption]
  );

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    setSelectedEncumbranceTypeOption((prev) =>
      prev ? findOptionByValue(selectedEncumbranceType) : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const encumbranceTypeOptions = encumbranceOptions.map(mapOption);

  const handleChangeEncumbranceTypeOption = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedEncumbranceType(
      (selectedOption?.value as EncumbranceTypes) || ''
    );
    setSelectedEncumbranceTypeOption(selectedOption);
  };

  return {
    data: {
      selectedEncumbranceTypeOption,
      encumbranceTypeOptions,
    },
    actions: {
      handleChangeEncumbranceTypeOption,
    },
  };
};
